import React, { useState, useRef } from 'react';
import axios from '../axios';
import { ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../shadcn/ui/dialog';
import { useToast } from '../shadcn/ui/use-toast';
import { Button } from '../shadcn/ui/button';
import { Label } from "../shadcn/ui/label";
import { Textarea } from "../shadcn/ui/textarea"

const CustomFileInputDialog = ({ onUploadSuccess }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isOpenMediaDialog, setIsOpenMediaDialog] = useState(false);
  const fileInputRef = useRef(null); // Ref for the file input
  const { toast } = useToast();

  const handleFileChange = (e) => {
    let selectedFiles = Array.from(e.target.files);
    const filesWithDetails = selectedFiles.map(file => ({
      file,
      title: '',
      description: '',
      alt: '',
    }));

    setFiles(filesWithDetails);
    setIsOpenMediaDialog(true);
  };

  const handleDetailChange = (index, key, value) => {
    setFiles(prevFiles => {
      const updatedFiles = [...prevFiles];
      updatedFiles[index][key] = value;
      return updatedFiles;
    });
  };

  const handleRemoveFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    // Validate files for empty fields
    for (const { title, description, alt } of files) {
      if (!title || !description || !alt) {
        toast({
          title: 'Σφάλμα επικύρωσης',
          description: 'Συμπληρώστε όλα τα πεδία (τίτλος, περιγραφή, alt) για κάθε αρχείο.',
          variant: 'error',
        });
        return; // Stop the upload process if validation fails
      }
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    files.forEach(({ file, title, description, alt }) => {
      formData.append('files[]', file);
      formData.append('titles[]', title);
      formData.append('descriptions[]', description);
      formData.append('alts[]', alt);
    });

    try {
      const response = await axios.post('/api/upload-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      toast({
        title: 'Success',
        description: 'Τα αρχεία μεταφορτώθηκαν με επιτυχία',
        variant: 'success',
      });

      if (onUploadSuccess) {
        onUploadSuccess(response.data);
        resetFiles(); // Reset files after upload
      }

    } catch (error) {
      toast({
        title: 'Error',
        description: 'Παρουσιάστηκε σφάλμα κατά τη μεταφόρτωση αρχείων',
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };


  const handleClick = () => {
    fileInputRef.current.click(); // Use ref to trigger file input
  };

  const resetFiles = () => {
    setFiles([]); // Clear file details
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input value
    }
    setIsOpenMediaDialog(false);
  };

  return (
    <div className="custom-file-input w-full">
      <div className="flex justify-end">
        <input
          ref={fileInputRef} // Assign ref to the input
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleFileChange}
          disabled={isUploading}
          className="hidden"
        />

        <Button
          onClick={handleClick}
          className="flex items-center space-x-2"
          disabled={isUploading}
        >
          <ArrowUpTrayIcon className="h-5 w-5" />
          <span>Μεταφόρτωση Αρχείων</span>
        </Button>
      </div>

      {files.length > 0 && (
        <Dialog
          open={isOpenMediaDialog}
          onOpenChange={(open) => {
            if (!open) resetFiles();
          }}
          className="fixed inset-0 flex items-center justify-center z-50"
        >
          <DialogContent className="media-dialog-content w-full max-w-6xl mx-auto">
            <DialogHeader>
              <DialogTitle>Προσθήκη {files.length > 1 ? 'Πολυμέσων' : 'Πολυμέσου'}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {files.map((fileDetail, index) => (
                <div key={index} className="flex flex-col md:flex-row items-start gap-10 overflow-auto">

                  <div className="flex-shrink-0 w-[400px] h-[400px] bg-gray-100 rounded overflow-hidden">
                    <img
                      src={URL.createObjectURL(fileDetail.file)}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex-grow">
                    <Label htmlFor="title" className="block text-left mb-2">
                      Τίτλος
                    </Label>
                    <input
                      id="title"
                      type="text"
                      placeholder="Τίτλος"
                      value={fileDetail.title}
                      onChange={(e) =>
                        handleDetailChange(index, "title", e.target.value)
                      }
                      className="h-10 border rounded p-2 w-full mb-4"
                    />

                    <Label htmlFor="description" className="block text-left mb-2">
                      Περιγραφή
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Περιγραφή"
                      value={fileDetail.description}
                      rows={6}
                      onChange={(e) =>
                        handleDetailChange(index, "description", e.target.value)
                      }
                      className="border rounded p-2 w-full mb-4"
                    />

                    <Label htmlFor="alt" className="block text-left mb-2">
                      Εναλλακτικό κείμενο  (Alt)
                    </Label>
                    <input
                      id="alt"
                      type="text"
                      placeholder="Εναλλακτικό κείμενο"
                      value={fileDetail.alt}
                      onChange={(e) =>
                        handleDetailChange(index, "alt", e.target.value)
                      }
                      className="h-10 border rounded p-2 w-full"
                    />

                  </div>
                </div>
              ))}
              <div className="flex justify-end">
                <Button
                  className="mt-4"
                  onClick={handleUpload}
                  disabled={isUploading}
                >
                  {isUploading ? "Μεταφόρτωση..." : "Μεταφόρτωση"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {isUploading && (
        <div className="mt-2">
          <span className="text-gray-500">Μεταφόρτωση... {uploadProgress}%</span>
          <div className="relative pt-1">
            <div className="flex h-2 bg-gray-200 rounded">
              <div
                className="bg-teal-600 rounded"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default CustomFileInputDialog;
